import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { barterApi, barterSearchPreferencesApi } from '@/services/api'
import { useNavigate } from 'react-router-dom'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Pagination from '@/components/ui/Pagination'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import LocationSelect from '@/components/forms/LocationSelect'
import PageNotification from '@/components/ui/PageNotification'
import { formatCurrency, formatDate } from '@/utils/format'
import { Search, Plus, Eye, Users, ArrowUp, ArrowDown, Info } from 'lucide-react'
import type { Barter } from '@/types'

type SortColumn = 'title' | 'type' | 'dollar_value' | 'status' | 'created_at' | 'city' | 'country'
type SortOrder = 'asc' | 'desc'

export default function BarterList() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [continentFilter, setContinentFilter] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState<SortColumn>('created_at')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [preferencesLoaded, setPreferencesLoaded] = useState(false)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  // Load saved search preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await barterSearchPreferencesApi.get()
        const prefs = response.data?.preferences

        if (prefs) {
          // Apply saved preferences
          setSearch(prefs.search || '')
          setDebouncedSearch(prefs.search || '')
          setTypeFilter(prefs.type_filter || '')
          setStatusFilter(prefs.status_filter || '')

          // Handle location filters - if continent is missing but other location filters exist, clear them
          // This ensures cascading dropdowns work properly
          const continent = prefs.continent_filter || ''
          const country = prefs.country_filter || ''
          const state = prefs.state_filter || ''
          const city = prefs.city_filter || ''

          if (!continent && (country || state || city)) {
            // Clear location filters if continent is missing
            setContinentFilter('')
            setCountryFilter('')
            setStateFilter('')
            setCityFilter('')
          } else {
            setContinentFilter(continent)
            setCountryFilter(country)
            setStateFilter(state)
            setCityFilter(city)
          }

          setSortBy(prefs.sort_by || 'created_at')
          setSortOrder(prefs.sort_order || 'desc')
        }
        setPreferencesLoaded(true)
      } catch (error) {
        console.error('Failed to load search preferences:', error)
        setPreferencesLoaded(true)
      }
    }

    loadPreferences()
  }, [])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      if (search !== debouncedSearch) {
        setCurrentPage(1) // Reset to page 1 on new search
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [search])

  // Save preferences when filters change (but only after the user actively
  // changes a filter — first-load defaults from the user's profile stay live
  // until then, so profile updates flow through on subsequent visits)
  useEffect(() => {
    if (!preferencesLoaded) return
    if (!hasUserInteracted) return

    const savePreferences = async () => {
      try {
        await barterSearchPreferencesApi.save({
          search: debouncedSearch,
          type_filter: typeFilter,
          status_filter: statusFilter,
          continent_filter: continentFilter,
          country_filter: countryFilter,
          state_filter: stateFilter,
          city_filter: cityFilter,
          sort_by: sortBy,
          sort_order: sortOrder,
        })
      } catch (error) {
        // Silently fail - preferences are not critical
        console.error('Failed to save search preferences:', error)
      }
    }

    // Debounce the save operation
    const timer = setTimeout(() => {
      savePreferences()
    }, 1000)

    return () => clearTimeout(timer)
  }, [preferencesLoaded, hasUserInteracted, debouncedSearch, typeFilter, statusFilter, continentFilter, countryFilter, stateFilter, cityFilter, sortBy, sortOrder])

  const { data, isLoading } = useQuery({
    queryKey: ['barters', {
      search: debouncedSearch,
      type: typeFilter,
      status: statusFilter,
      continent: continentFilter,
      country: countryFilter,
      state: stateFilter,
      city: cityFilter,
      page: currentPage,
      sort_by: sortBy,
      sort_order: sortOrder,
    }],
    queryFn: () => barterApi.list({
      search: debouncedSearch,
      type: typeFilter,
      status: statusFilter,
      continent: continentFilter,
      country: countryFilter,
      state: stateFilter,
      city: cityFilter,
      page: currentPage,
      limit: 10,
      sort_by: sortBy,
      sort_order: sortOrder
    }),
    placeholderData: (previousData) => previousData,
  })

  const barters = (data?.data as any)?.barters || []
  const totalPages = (data?.data as any)?.pages || 1

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
    setCurrentPage(1) // Reset to first page when sorting changes
    setHasUserInteracted(true)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setHasUserInteracted(true)
  }

  const handleFilterChange = (filterType: 'type' | 'status', value: string) => {
    if (filterType === 'type') {
      setTypeFilter(value)
    } else {
      setStatusFilter(value)
    }
    setCurrentPage(1) // Reset to first page when filter changes
    setHasUserInteracted(true)
  }

  // Check if user can create a barter request for this barter
  const canRequestBarter = (barter: Barter) => {
    if (barter.status !== 'open') return false
    return true
  }

  const handleRequestBarter = (barterId: number) => {
    navigate(`/dashboard/barters/${barterId}`)
  }

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortBy !== column) {
      return <ArrowUp className="w-3 h-3 text-gray-300" />
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-primary-600" />
    ) : (
      <ArrowDown className="w-3 h-3 text-primary-600" />
    )
  }

  if (isLoading && !data) return <Loading />

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Barter Marketplace</h1>
          <p className="text-gray-600 mt-1">Browse and discover barters</p>
        </div>
        <Button onClick={() => navigate('/dashboard/barters/create')}>
          <Plus className="w-4 h-4 mr-2" />
          Create Barter
        </Button>
      </div>

      <PageNotification pageRoute="barters" />

      <Card>
        <div className="space-y-4 mb-6">
          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 min-w-0">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search barters..."
                  className="input pl-10 w-full"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
            <select className="select w-full md:w-auto" value={typeFilter} onChange={(e) => handleFilterChange('type', e.target.value)}>
              <option value="">All Types</option>
              <option value="goods">Goods</option>
              <option value="services">Services</option>
            </select>
            <select className="select w-full md:w-auto" value={statusFilter} onChange={(e) => handleFilterChange('status', e.target.value)}>
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="ready">Ready</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Location Filters */}
          <LocationSelect
            continent={continentFilter}
            country={countryFilter}
            state={stateFilter}
            city={cityFilter}
            onContinentChange={(value) => {
              setContinentFilter(value)
              setCountryFilter('')
              setStateFilter('')
              setCityFilter('')
              setCurrentPage(1)
              setHasUserInteracted(true)
            }}
            onCountryChange={(value) => {
              setCountryFilter(value)
              setStateFilter('')
              setCityFilter('')
              setCurrentPage(1)
              setHasUserInteracted(true)
            }}
            onStateChange={(value) => {
              setStateFilter(value)
              setCityFilter('')
              setCurrentPage(1)
              setHasUserInteracted(true)
            }}
            onCityChange={(value) => {
              setCityFilter(value)
              setCurrentPage(1)
              setHasUserInteracted(true)
            }}
            required={false}
          />
        </div>

        <ResponsiveTable>
          <table className="table">
            <thead className="table-header">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Title</span>
                    <SortIcon column="title" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                <th
                  className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('type')}
                  data-priority="4"
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    <SortIcon column="type" />
                  </div>
                </th>
                <th
                  className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('dollar_value')}
                  data-priority="3"
                >
                  <div className="flex items-center space-x-1">
                    <span>Value</span>
                    <SortIcon column="dollar_value" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('city')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Location</span>
                    <SortIcon column="city" />
                  </div>
                </th>
                <th
                  className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('status')}
                  data-priority="2"
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    <SortIcon column="status" />
                  </div>
                </th>
                <th
                  className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('created_at')}
                  data-priority="1"
                >
                  <div className="flex items-center space-x-1">
                    <span>Created</span>
                    <SortIcon column="created_at" />
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : barters.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <p className="text-gray-500 mb-4">No barters found</p>
                    <Button onClick={() => navigate('/dashboard/barters/create')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Barter
                    </Button>
                  </td>
                </tr>
              ) : (
                barters.map((barter: Barter) => (
                  <tr key={barter.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className="font-medium text-gray-900 hover:text-primary-600 cursor-pointer"
                        onClick={() => navigate(`/dashboard/barters/${barter.id}`)}
                      >
                        {barter.title}
                      </div>
                      <div className="text-sm text-gray-500">{barter.description?.substring(0, 50)}...</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/dashboard/users/${barter.user_id}`)}
                        className="text-sm text-primary-600 hover:text-primary-800 hover:underline font-medium"
                      >
                        {barter.first_name} {barter.last_name}
                      </button>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="4" data-label="Type">
                      <Badge variant={barter.type === 'goods' ? 'primary' : 'success'}>
                        {barter.type}
                      </Badge>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap font-medium" data-priority="3" data-label="Value">
                      {formatCurrency(barter.dollar_value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {barter.city || 'Any'}
                      {barter.state && <>, {barter.state}</>}
                      , {barter.country || 'Any'}
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="2" data-label="Status">
                      <Badge status={barter.status}>
                        {barter.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-priority="1" data-label="Created">
                      {formatDate(barter.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          className="mobile-details-trigger text-blue-600 hover:text-blue-900"
                          title="View Hidden Details"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/dashboard/barters/${barter.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {canRequestBarter(barter) && (
                          <button
                            onClick={() => handleRequestBarter(barter.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Request Barter / Start Group"
                          >
                            <Users className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ResponsiveTable>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

    </div>
  )
}
